/**
 * MSAL authentication service for Entra External ID integration.
 * Wraps @azure/msal-browser PublicClientApplication with PKCE flow.
 */
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
  type AuthenticationResult,
  type Configuration,
  type RedirectRequest,
  type SilentRequest,
} from '@azure/msal-browser';
import { environment } from '../environments/environment';
import { rememberCurrentAppPathForPostAuth } from '../auth-navigation';

/**
 * MSAL configuration using PKCE flow.
 */
const msalConfig: Configuration = {
  auth: {
    clientId: environment.auth.clientId,
    authority: `https://${environment.auth.authorityDomain}/${environment.auth.tenantId}/`,
    redirectUri: environment.auth.redirectUri,
    postLogoutRedirectUri: environment.auth.postLogoutRedirectUri,
    knownAuthorities: [environment.auth.authorityDomain],
  },
  cache: {
    cacheLocation: 'sessionStorage',
  },
};

/**
 * Singleton MSAL instance.
 */
export const msalInstance = new PublicClientApplication(msalConfig);

let msalInitializationPromise: Promise<void> | null = null;

async function ensureMsalInitialized(): Promise<void> {
  msalInitializationPromise ??= msalInstance.initialize();
  await msalInitializationPromise;
}

function readFirstAccount(): AccountInfo | null {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    return null;
  }

  return accounts[0];
}

function getRoleFromClaims(claims: Record<string, unknown> | null | undefined): string | null {
  if (!claims) {
    return null;
  }

  const role = claims['extension_Role'] || claims['role'] || claims['roles'];
  if (typeof role === 'string') {
    return role;
  }
  if (Array.isArray(role) && role.length > 0) {
    return role[0] as string;
  }

  return null;
}

function decodeJwtClaims(token: string): Record<string, unknown> | null {
  const [, payload] = token.split('.');
  if (!payload) {
    return null;
  }

  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const decoded = atob(padded);
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function getCachedAccessTokenRole(account: AccountInfo | null): string | null {
  if (!account) {
    return null;
  }

  try {
    for (let index = 0; index < sessionStorage.length; index += 1) {
      const key = sessionStorage.key(index);
      if (!key || !key.includes('|accesstoken|')) {
        continue;
      }

      const rawEntry = sessionStorage.getItem(key);
      if (!rawEntry) {
        continue;
      }

      const parsed = JSON.parse(rawEntry) as {
        credentialType?: string;
        clientId?: string;
        homeAccountId?: string;
        realm?: string;
        target?: string;
        secret?: string;
      };

      if (
        parsed.credentialType !== 'AccessToken' ||
        parsed.clientId !== environment.auth.clientId ||
        parsed.homeAccountId !== account.homeAccountId ||
        parsed.realm !== account.tenantId ||
        !parsed.target?.includes(environment.auth.apiScope) ||
        typeof parsed.secret !== 'string'
      ) {
        continue;
      }

      const claims = decodeJwtClaims(parsed.secret);
      const role = getRoleFromClaims(claims);
      if (role) {
        return role;
      }
    }
  } catch {
    return null;
  }

  return null;
}

/**
 * Initialize MSAL instance (must be called before any other MSAL operations).
 */
export async function initializeMsal(): Promise<void> {
  await ensureMsalInitialized();
}

/**
 * Handle redirect promise after MSAL redirects back to the app.
 * Call this during app startup to process auth callbacks.
 * @returns Authentication result if redirect was from login, null otherwise
 */
export async function handleRedirectCallback(): Promise<AuthenticationResult | null> {
  await ensureMsalInitialized();
  return await msalInstance.handleRedirectPromise({
    navigateToLoginRequestUrl: false,
  });
}

/**
 * Get the currently signed-in account, if any.
 */
export async function getAccount(): Promise<AccountInfo | null> {
  await ensureMsalInitialized();
  return readFirstAccount();
}

/**
 * Initiate sign-in redirect flow.
 * @param loginHint Optional email hint for pre-filling the login form
 */
export async function loginRedirect(loginHint?: string): Promise<void> {
  await ensureMsalInitialized();
  rememberCurrentAppPathForPostAuth();

  const request: RedirectRequest = {
    scopes: [environment.auth.apiScope],
    loginHint,
  };
  await msalInstance.loginRedirect(request);
}

/**
 * Initiate logout redirect flow.
 */
export async function logoutRedirect(): Promise<void> {
  await ensureMsalInitialized();
  const account = readFirstAccount();
  await msalInstance.logoutRedirect({
    account: account ?? undefined,
  });
}

/**
 * Acquire an access token silently (from cache or via silent refresh).
 * Falls back to interactive login if silent acquisition fails.
 * @returns Access token string
 */
export async function getAccessToken(): Promise<string | null> {
  await ensureMsalInitialized();

  const account = readFirstAccount();
  if (!account) {
    return null;
  }

  const request: SilentRequest = {
    scopes: [environment.auth.apiScope],
    account,
  };

  try {
    const response = await msalInstance.acquireTokenSilent(request);
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Silent token acquisition failed, need to trigger interactive flow
      // Redirect to login
      await loginRedirect(account.username);
      return null;
    }
    console.error('Token acquisition failed:', error);
    return null;
  }
}

/**
 * Extract user email from account info.
 */
export function getUserEmail(account: AccountInfo | null): string | null {
  if (!account) {
    return null;
  }
  return account.username || account.name || null;
}

/**
 * Extract role from account claims.
 * The role claim is expected in the account's idTokenClaims.
 */
export function getUserRole(account: AccountInfo | null): string | null {
  if (!account) {
    return null;
  }

  const idTokenRole = getRoleFromClaims(account.idTokenClaims as Record<string, unknown> | undefined);
  if (idTokenRole) {
    return idTokenRole;
  }

  return getCachedAccessTokenRole(account);
}
